import type { CustomizationPayload, ProfileCustomization } from '../../lib/types';
import { Badge } from '../badges/Badge';
import { Ribbon } from '../ribbons/Ribbon';
import { MotifLayer } from '../badges/badgeArt';
import { Panel } from '../ui';

/**
 * Choosing what the profile header shows.
 *
 * Four blocks — badges, ribbons, accolades, stats — each an ordered shortlist
 * with a hard cap. The shape is the same in all four because they behave the
 * same way: click to add, click again to remove, and **position in the list is
 * the arrangement**, so slot one is what somebody leads with.
 *
 * ## Why clicking, not dragging
 *
 * The brief suggests drag-and-drop reordering. Click-to-append gets the same
 * result with none of the cost: a drag needs a drop target, a drag ghost, a
 * keyboard equivalent and a touch story, and on a phone — where this list is
 * three items in a scrolling row — dragging is the worse interaction anyway.
 * Order is the order you picked, and removing an entry closes the gap. Adding
 * real reordering later is a change to one component, because the wire format
 * is already an ordered array.
 *
 * ## What is not decided here
 *
 * Whether an option may be picked at all. Every list comes from
 * `headerOptions` on the server, and `PATCH /me/customization` re-checks each
 * choice against the same functions — so an editor offering something the save
 * refuses is impossible by construction rather than by two people remembering.
 * That is the rule `game/cosmetics.ts` was written to enforce.
 */

type Options = CustomizationPayload['headerOptions'];

export function HeaderEditor({
  options,
  draft,
  onChange,
}: {
  options: Options;
  draft: ProfileCustomization;
  onChange: <K extends keyof ProfileCustomization>(
    key: K,
    value: ProfileCustomization[K],
  ) => void;
}) {
  /**
   * Add, remove, or refuse because the list is full.
   *
   * Refusing rather than silently dropping the oldest: a picker that quietly
   * evicts slot one when you tap a seventh badge is a picker that loses the
   * choice somebody just made.
   */
  const toggle = (list: string[], id: string, limit: number): string[] | null => {
    if (list.includes(id)) return list.filter((entry) => entry !== id);
    if (list.length >= limit) return null;
    return [...list, id];
  };

  const earnedAccolades = options.accolades.filter((entry) => entry.earned);

  return (
    <div className="stack">
      <p className="tiny muted" style={{ margin: 0 }}>
        These fill the top of your profile, beside your trainer card. Leave a block empty and
        it picks your best automatically — it will never show a blank space.
      </p>

      {/* --- Badges ------------------------------------------------------- */}
      <Panel raised={false}>
        <SlotHead
          label="Featured Badges"
          count={draft.featuredBadges.length}
          limit={options.limits.badges}
          onClear={() => onChange('featuredBadges', [])}
        />
        {options.badges.length === 0 ? (
          <p className="tiny muted" style={{ margin: 0 }}>
            You haven't earned a badge yet. Every one of them is already counting.
          </p>
        ) : (
          <div className="hedit__grid">
            {options.badges.map((badge) => {
              const at = draft.featuredBadges.indexOf(badge.key);
              return (
                <PickTile
                  key={badge.key}
                  slot={at}
                  label={badge.name}
                  sub={badge.tierName ?? undefined}
                  onClick={() => {
                    const next = toggle(draft.featuredBadges, badge.key, options.limits.badges);
                    if (next) onChange('featuredBadges', next);
                  }}
                >
                  <Badge badge={badge} size={44} still />
                </PickTile>
              );
            })}
          </div>
        )}
      </Panel>

      {/* --- Ribbons ------------------------------------------------------ */}
      <Panel raised={false}>
        <SlotHead
          label="Featured Ribbons"
          count={draft.featuredRibbons.length}
          limit={options.limits.ribbons}
          onClear={() => onChange('featuredRibbons', [])}
        />
        {options.ribbons.length === 0 ? (
          <p className="tiny muted" style={{ margin: 0 }}>
            Your Pokémon earn ribbons as they grow — for being fed, for being bred from, for
            what they live through.
          </p>
        ) : (
          <div className="hedit__grid">
            {options.ribbons.map((ribbon) => {
              const at = draft.featuredRibbons.indexOf(ribbon.id);
              return (
                <PickTile
                  key={ribbon.id}
                  slot={at}
                  label={ribbon.name}
                  // The Pokemon, always. A ribbon list without the Pokemon on it
                  // is a list of achievements, which is what badges already are.
                  sub={ribbon.on}
                  onClick={() => {
                    const next = toggle(draft.featuredRibbons, ribbon.id, options.limits.ribbons);
                    if (next) onChange('featuredRibbons', next);
                  }}
                >
                  <Ribbon ribbon={ribbon} size={36} />
                </PickTile>
              );
            })}
          </div>
        )}
      </Panel>

      {/* --- Accolades ---------------------------------------------------- */}
      <Panel raised={false}>
        <SlotHead
          label="Accolades"
          count={draft.featuredAccolades.length}
          limit={options.limits.accolades}
          onClear={() => onChange('featuredAccolades', [])}
        />

        {earnedAccolades.length === 0 ? (
          <p className="tiny muted" style={{ margin: 0 }}>
            Finish a Pokédex, a museum wing or a badge ladder and it will appear here.
          </p>
        ) : (
          <div className="row row--wrap" style={{ gap: 'var(--gap-xs)' }}>
            {earnedAccolades.map((accolade) => {
              const on = draft.featuredAccolades.includes(accolade.key);
              return (
                <button
                  key={accolade.key}
                  type="button"
                  className={`accolade hedit__accolade ${on ? 'hedit__accolade--on' : ''}`}
                  style={{ ['--accolade-accent' as string]: accolade.accent }}
                  title={`${accolade.description}\n${accolade.detail}`}
                  onClick={() => {
                    const next = toggle(
                      draft.featuredAccolades,
                      accolade.key,
                      options.limits.accolades,
                    );
                    if (next) onChange('featuredAccolades', next);
                  }}
                >
                  <svg viewBox="0 0 16 16" width={16} height={16} shapeRendering="crispEdges" aria-hidden>
                    <MotifLayer motif={accolade.motif} fill="currentColor" highlight="#fff" />
                  </svg>
                  <span className="accolade__label">{accolade.label}</span>
                </button>
              );
            })}
          </div>
        )}

        {/* Unearned ones, shown with what is left to do. The same decision the
            cosmetics editor makes about locked nameplates: a thing you can see
            the shape of is what makes you want it. */}
        {options.accolades.some((entry) => !entry.earned) && (
          <details className="locked" style={{ marginTop: 'var(--gap-xs)' }}>
            <summary className="tiny muted">
              {options.accolades.filter((entry) => !entry.earned).length} still to earn
            </summary>
            <div className="stack-sm" style={{ marginTop: 'var(--gap-xs)' }}>
              {options.accolades
                .filter((entry) => !entry.earned)
                .map((accolade) => (
                  <div key={accolade.key} className="row row--between tiny">
                    <span className="muted">{accolade.label}</span>
                    <span className="mono muted">{accolade.detail}</span>
                  </div>
                ))}
            </div>
          </details>
        )}
      </Panel>

      {/* --- Stats -------------------------------------------------------- */}
      <Panel raised={false}>
        <SlotHead
          label="Highlights"
          count={draft.featuredStats.length}
          limit={options.limits.stats}
          onClear={() => onChange('featuredStats', [])}
        />
        <p className="tiny muted" style={{ margin: 0 }}>
          Four numbers, in the order you pick them.
        </p>

        {/* Grouped exactly as the statistics page groups them, because they are
            the same catalog — a stat cannot be filed under "Collection" there
            and somewhere else here. */}
        {[...new Set(options.stats.map((entry) => entry.group))].map((group) => (
          <div key={group} className="stack-sm" style={{ marginTop: 'var(--gap-xs)' }}>
            <span className="tiny mono muted">{group.toUpperCase()}</span>
            <div className="row row--wrap" style={{ gap: 4 }}>
              {options.stats
                .filter((entry) => entry.group === group)
                .map((stat) => {
                  const at = draft.featuredStats.indexOf(stat.key);
                  return (
                    <button
                      key={stat.key}
                      type="button"
                      className={`chip chip--sm ${at >= 0 ? 'chip--on' : ''}`}
                      onClick={() => {
                        const next = toggle(draft.featuredStats, stat.key, options.limits.stats);
                        if (next) onChange('featuredStats', next);
                      }}
                    >
                      {at >= 0 && <span className="mono">{at + 1}. </span>}
                      {stat.label}
                    </button>
                  );
                })}
            </div>
          </div>
        ))}
      </Panel>
    </div>
  );
}

/* -------------------------------------------------------------------------
   Pieces
   ------------------------------------------------------------------------- */

function SlotHead({
  label,
  count,
  limit,
  onClear,
}: {
  label: string;
  count: number;
  limit: number;
  onClear: () => void;
}) {
  return (
    <div className="row row--between" style={{ marginBottom: 'var(--gap-xs)' }}>
      <span className="tiny mono muted">{label.toUpperCase()}</span>
      <span className="row" style={{ gap: 'var(--gap-sm)', alignItems: 'baseline' }}>
        <span className="tiny muted mono">
          {count} / {limit}
        </span>
        {count > 0 && (
          <button type="button" className="linkbtn tiny" onClick={onClear}>
            Clear
          </button>
        )}
      </span>
    </div>
  );
}

/**
 * One option in a picker.
 *
 * The slot number is the important part of the selected state: with an ordered
 * list, "selected" is not enough information — a player needs to see that this
 * badge is the one that will sit first.
 */
function PickTile({
  slot,
  label,
  sub,
  onClick,
  children,
}: {
  slot: number;
  label: string;
  sub?: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  const on = slot >= 0;
  return (
    <button
      type="button"
      className={`hedit__tile ${on ? 'hedit__tile--on' : ''}`}
      onClick={onClick}
      title={label}
      aria-pressed={on}
    >
      {on && <span className="hedit__slot mono">{slot + 1}</span>}
      {children}
      <span className="hedit__label tiny">{label}</span>
      {sub && <span className="hedit__sub tiny muted">{sub}</span>}
    </button>
  );
}
